import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon } from '@heroicons/react/24/outline';
import BannerCard from '../components/BannerCard';
import BannerModal from '../components/BannerModal';
import ConfirmDialog from '../components/ConfirmDialog';

const GalleryBannersPage = () => {
    const [banners, setBanners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, bannerId: null });

    // Fetch banners (filtered for gallery page type)
    const fetchBanners = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/banners');
            // Filter for gallery page type only
            const filteredBanners = (response.data.data || []).filter(
                banner => banner.page_type === 'gallery'
            );
            setBanners(filteredBanners);
        } catch (error) {
            console.error('Error fetching banners:', error);
            toast.error('Failed to load banners');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBanners();
    }, []);

    // Handle add new banner
    const handleAddNew = () => {
        setEditingBanner(null);
        setIsModalOpen(true);
    };

    // Handle edit banner
    const handleEdit = (banner) => {
        setEditingBanner(banner);
        setIsModalOpen(true);
    };

    // Handle delete banner
    const handleDelete = (bannerId) => {
        setDeleteConfirm({ isOpen: true, bannerId });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/banners/${deleteConfirm.bannerId}`);
            toast.success('Banner deleted successfully');
            fetchBanners();
        } catch (error) {
            console.error('Error deleting banner:', error);
            toast.error('Failed to delete banner');
        }
    };

    // Handle toggle active status
    const handleToggleActive = async (banner) => {
        try {
            await axios.put(`/api/admin/banners/${banner.id}`, {
                ...banner,
                is_active: !banner.is_active,
                highlights: typeof banner.highlights === 'string'
                    ? banner.highlights
                    : JSON.stringify(banner.highlights)
            });
            toast.success(`Banner ${!banner.is_active ? 'activated' : 'deactivated'} successfully`);
            fetchBanners();
        } catch (error) {
            console.error('Error toggling banner status:', error);
            toast.error('Failed to update banner status');
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Gallery Banners</h1>
                    <p className="text-slate-500 mt-1">Manage banners for the Gallery (Aham Moments) page</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Banner
                </button>
            </div>

            {/* Banners List */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-64 bg-slate-100 rounded-2xl animate-pulse"></div>
                    ))}
                </div>
            ) : banners.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border-2 border-dashed border-slate-200">
                    <p className="text-slate-500 text-lg">No banners found. Click "Add New Banner" to create one.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {banners.map((banner) => (
                        <BannerCard
                            key={banner.id}
                            banner={banner}
                            onEdit={handleEdit}
                            onDelete={handleDelete}
                            onToggleActive={handleToggleActive}
                        />
                    ))}
                </div>
            )}

            {/* Banner Modal */}
            <BannerModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingBanner(null);
                }}
                banner={editingBanner}
                defaultPageType="gallery"
                onSave={fetchBanners}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, bannerId: null })}
                onConfirm={confirmDelete}
                title="Delete Banner"
                message="Are you sure you want to delete this banner? This action cannot be undone."
            />
        </div>
    );
};

export default GalleryBannersPage;
